import { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, X } from "lucide-react";
import StateSelector from "@/components/StateSelector";
import UserSelector from "@/components/UserSelector";
import ModalShell from "@/components/ModalShell";

const CATEGORIES = ["unassigned", "product", "strain", "marketing", "retail", "sales", "merch"];

export default function EditAssetModal({ open, asset, onClose, onSaved }) {
  const [form, setForm] = useState({ title: "", description: "", category: "marketing", subcategory: "", tags: "", states: "", allowed_user_ids: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (asset) {
      setForm({
        title: asset.title || "",
        description: asset.description || "",
        category: asset.category || "marketing",
        subcategory: asset.subcategory || "",
        tags: (asset.tags || []).join(", "),
        states: (asset.states || []).join(", "),
        allowed_user_ids: (asset.allowed_user_ids || []).join(", "),
      });
    }
  }, [asset]);

  if (!open || !asset) return null;

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/assets/${asset.id}`, {
        title: form.title,
        description: form.description,
        category: form.category,
        subcategory: form.subcategory,
        tags: form.tags,
        states: form.states,
        allowed_user_ids: form.allowed_user_ids,
      });
      toast.success("Asset updated");
      onSaved && onSaved(data);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusy(false);
  }

  return (
    <ModalShell open={open && !!asset} onClose={onClose} maxWidth="max-w-xl" testId="edit-modal">
      <button onClick={onClose} className="absolute top-4 right-4 p-1 hover:bg-surface-elev rounded-sm" data-testid="edit-close" aria-label="Close">
        <X className="h-4 w-4" />
      </button>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">Edit Asset</div>
      <h2 className="font-display text-2xl uppercase font-bold tracking-tight truncate">{asset?.title}</h2>

      <form onSubmit={submit} className="mt-6 space-y-5">
        <Field label="Title">
          <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="edit-title" className="w-full bg-transparent border-b border-border py-2 text-sm outline-none focus:border-foreground" />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Category">
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="edit-cat" className="w-full bg-transparent border-b border-border py-2 text-sm outline-none focus:border-foreground capitalize">
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Subcategory">
            <input value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} data-testid="edit-sub" className="w-full bg-transparent border-b border-border py-2 text-sm outline-none focus:border-foreground" />
          </Field>
        </div>
        <Field label="Description">
          <textarea rows={6} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="edit-desc" placeholder="Full description — line breaks and paragraphs will be preserved." className="w-full bg-transparent border border-border rounded-sm p-3 text-sm outline-none focus:border-foreground whitespace-pre-wrap" />
        </Field>
        <div className="grid grid-cols-1 gap-4">
          <Field label="Keywords (comma-separated, for search)">
            <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="edit-tags" placeholder="e.g. summer, promo, holiday" className="w-full bg-transparent border-b border-border py-2 text-sm outline-none focus:border-foreground" />
          </Field>
        </div>
        <StateSelector
          value={form.states}
          onChange={(str) => setForm({ ...form, states: str })}
          testIdPrefix="edit-state"
        />
        <UserSelector
          value={form.allowed_user_ids}
          onChange={(str) => setForm({ ...form, allowed_user_ids: str })}
          testIdPrefix="edit-users"
        />
        <button type="submit" disabled={busy} className="w-full bg-foreground text-background py-3 uppercase tracking-widest text-xs font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="edit-submit">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          Save Changes
        </button>
      </form>
    </ModalShell>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-1 block">{label}</label>
      {children}
    </div>
  );
}
